import { inject, Injectable } from '@angular/core'
import { OrganizationsService } from '@ftr/api-core'
import { COUNTRY_TO_REGION } from '@ftr/api-external-organization'
import { Organization } from '@ftr/contracts/api/organizations'
import { UserRole, UserStatus } from '@ftr/contracts/api/user'
import { Region } from '@ftr/contracts/type/court'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, RemoteData } from '@ftr/foundation'
import { asOrganizationId, ResetTenantContextAction, SetTenantContextAction, TenantType } from '@ftr/ui-tenant'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { Observable, of, tap } from 'rxjs'
import {
  FetchOrganizationsAction,
  OrganizationUpdatedAction,
  ResetOrganizationStateAction,
  SelectOrganizationAction,
} from './organization.actions'
import { OrganizationStateModel } from './organization.model'

export function defaultOrganizationState(): OrganizationStateModel {
  return {
    organizations: RemoteData.notAsked(),
    currentOrganizationId: undefined,
  }
}

@State<OrganizationStateModel>({
  name: 'organizationState',
  defaults: defaultOrganizationState(),
})
@Injectable()
export class OrganizationState {
  private readonly organizationsService = inject(OrganizationsService)

  @Selector()
  static organizations(state: OrganizationStateModel): RemoteData<Organization[]> {
    return state.organizations
  }

  @Selector()
  static currentOrganizationId(state: OrganizationStateModel): Uuid | undefined {
    return state.currentOrganizationId
  }

  @Selector()
  static currentUserRole(state: OrganizationStateModel): UserRole | undefined {
    return findCurrentOrganization(state)?.role
  }

  @Selector()
  static currentUserStatus(state: OrganizationStateModel): UserStatus | undefined {
    return findCurrentOrganization(state)?.status
  }

  @Selector()
  static currentOrganization(state: OrganizationStateModel): Organization | undefined {
    return findCurrentOrganization(state)
  }

  @Selector()
  static currentOrganizationRegion(state: OrganizationStateModel): Region | undefined {
    const countryCode = findCurrentOrganization(state)?.countryCode
    return countryCode ? COUNTRY_TO_REGION[countryCode] : undefined
  }

  @Action(FetchOrganizationsAction, { cancelUncompleted: true })
  fetchOrganizations(
    { getState, patchState }: StateContext<OrganizationStateModel>,
    { bypassCache }: FetchOrganizationsAction,
  ): ApiResult<Organization[]> {
    const { organizations } = getState()
    if (!bypassCache && (organizations.isSuccess() || organizations.isLoading())) {
      return of(organizations)
    }

    patchState({ organizations: RemoteData.loading() })

    return this.organizationsService.fetchForCurrentUser().pipe(tap(remote => patchState({ organizations: remote })))
  }

  @Action(SelectOrganizationAction)
  selectOrganization(
    ctx: StateContext<OrganizationStateModel>,
    { organizationId }: SelectOrganizationAction,
  ): Observable<unknown> {
    ctx.patchState({ currentOrganizationId: organizationId })

    if (!organizationId) {
      return of(undefined)
    }

    return ctx.dispatch(new FetchOrganizationsAction()).pipe(
      tap(() => {
        // Only set tenant context once we've actually resolved the organization (and
        // therefore its region) - not just its id. If the id doesn't match a real
        // organization (e.g. the user isn't a member), tenant context is left alone here;
        // the member/admin guards redirect away in that case.
        const organization = findCurrentOrganization(ctx.getState())

        if (organization) {
          ctx.dispatch(
            new SetTenantContextAction({
              type: TenantType.Organization,
              tenantId: asOrganizationId(organization.id),
              region: organization.region,
            }),
          )
        }
      }),
    )
  }

  @Action(OrganizationUpdatedAction)
  organizationUpdated(
    { getState, patchState }: StateContext<OrganizationStateModel>,
    { organization: updated }: OrganizationUpdatedAction,
  ): void {
    const { organizations } = getState()
    patchState({
      organizations: organizations.map(list =>
        list.map(organization =>
          organization.id === updated.id
            ? {
                ...organization,
                name: updated.name,
                countryCode: updated.countryCode,
                imageUrl: updated.imageUrl,
                timeZoneId: updated.timeZoneId,
                languageDialect: updated.languageDialect,
                subscriptionPlan: updated.subscriptionPlan,
              }
            : organization,
        ),
      ),
    })
  }

  @Action(ResetOrganizationStateAction)
  resetOrderState({ setState, dispatch }: StateContext<OrganizationStateModel>): void {
    setState(defaultOrganizationState())
    // `None` is only for the legacy court site. Clearing back to not-asked lets the next
    // workspace (for example `/individual`) set the tenant, instead of leaving `None` behind.
    dispatch(new ResetTenantContextAction())
  }
}

function findCurrentOrganization(organizationState: OrganizationStateModel): Organization | undefined {
  const { currentOrganizationId, organizations } = organizationState ?? {}
  return currentOrganizationId ? organizations.unwrapOr([]).find(({ id }) => id === currentOrganizationId) : undefined
}
